import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useMarkSlideViewedMutation,
  useStoryTracker,
  firstUnviewedSlideIndex,
  type StoryFeedItem,
  type StorySlide,
  type StoryOpenSource,
  type SlideExitReason,
  type LinkTargetType,
} from '@entities/stories'
import styles from './StoryViewer.module.css'

interface StoryViewerProps {
  /** Лента историй в том же порядке, что и в трее (непросмотренные — вперёд). */
  stories: StoryFeedItem[]
  /** Индекс истории в `stories`, с которой открылся просмотрщик (тап по кружку). */
  startIndex: number
  /** Вызывается при закрытии просмотрщика (крестик, Esc, докрутка до конца). */
  onClose: () => void
}

/** Сколько показывать картинку до автоперехода. */
const IMAGE_DURATION_MS = 5000
/** Дебаунс отметки слайда просмотренным. */
const MARK_DEBOUNCE_MS = 400

/**
 * Полноэкранный просмотрщик историй: листает слайды тапами/клавишами, отмечает
 * просмотр и пишет аналитику. См. {@link StoryViewerProps}.
 */
export const StoryViewer = ({ stories, startIndex, onClose }: StoryViewerProps) => {
  const navigate = useNavigate()
  const markMutation = useMarkSlideViewedMutation()
  const tracker = useStoryTracker()

  const [storyIndex, setStoryIndex] = useState(startIndex)
  const [slideIndex, setSlideIndex] = useState(() =>
    firstUnviewedSlideIndex(stories[startIndex]),
  )

  const story = stories[storyIndex]
  const slides: StorySlide[] = story?.slides ?? []
  const slide: StorySlide | undefined = slides[slideIndex]
  const hasAccess = Boolean(story?.has_access)

  // Момент показа текущего слайда — для dwell_ms аналитики.
  const slideShownAtRef = useRef(performance.now())

  // Стабильная ссылка на mutate, чтобы колбэки не зависели от состояния мутации.
  const markMutateRef = useRef(markMutation.mutate)
  markMutateRef.current = markMutation.mutate

  // --- Отметка слайда просмотренным (POST /stories/view) с дебаунсом ---
  const pendingRef = useRef<{ slideId: number; timer: number } | null>(null)

  const flushMark = useCallback(() => {
    const pending = pendingRef.current
    if (pending) {
      clearTimeout(pending.timer)
      markMutateRef.current(pending.slideId)
      pendingRef.current = null
    }
  }, [])

  const scheduleMark = useCallback((slideId: number) => {
    if (pendingRef.current) clearTimeout(pendingRef.current.timer)
    const timer = window.setTimeout(() => {
      markMutateRef.current(slideId)
      pendingRef.current = null
    }, MARK_DEBOUNCE_MS)
    pendingRef.current = { slideId, timer }
  }, [])

  // --- Аналитика: метаданные истории + переходы ---
  const storyMeta = useCallback(
    (index: number) => {
      const s = stories[index]
      return {
        storyId: s.story_id,
        storyPosition: index + 1,
        accessType: s.access_type,
        hasAccess: s.has_access,
      }
    },
    [stories],
  )

  /** Открытие истории (аналитика) + переход к ней в UI. */
  const openStoryAt = useCallback(
    (index: number, source: StoryOpenSource) => {
      tracker.openStory(storyMeta(index), source)
      setStoryIndex(index)
      setSlideIndex(firstUnviewedSlideIndex(stories[index]))
    },
    [tracker, storyMeta, stories],
  )

  /** Событие выхода со слайда (если есть доступ и слайд). */
  const leaveSlide = useCallback(
    (reason: SlideExitReason) => {
      if (!hasAccess || !slide) return
      tracker.viewSlide({
        slideId: slide.slide_id,
        slideIndex: slideIndex + 1,
        dwellMs: performance.now() - slideShownAtRef.current,
        exitReason: reason,
      })
    },
    [hasAccess, slide, slideIndex, tracker],
  )

  /** Закрыть текущую историю (аналитика). */
  const endCurrentStory = useCallback(() => {
    const viewedSomething = hasAccess && slides.length > 0
    tracker.closeStory({
      lastSlideIndex: viewedSomething ? slideIndex + 1 : 0,
      isCompleted: viewedSomething ? slideIndex >= slides.length - 1 : false,
    })
  }, [hasAccess, slides.length, slideIndex, tracker])

  /** Финальное закрытие: флаш отметок и батча аналитики. */
  const finish = useCallback(() => {
    flushMark()
    tracker.flush()
    onClose()
  }, [flushMark, tracker, onClose])

  const advance = useCallback(
    (reason: 'tap_forward' | 'auto') => {
      if (hasAccess && slideIndex < slides.length - 1) {
        leaveSlide(reason)
        setSlideIndex(slideIndex + 1)
        return
      }
      // Конец истории (или заглушка без доступа)
      leaveSlide(reason)
      endCurrentStory()
      if (storyIndex < stories.length - 1) {
        openStoryAt(storyIndex + 1, reason === 'auto' ? 'auto_next' : 'swipe_next')
      } else {
        finish()
      }
    },
    [
      hasAccess, slideIndex, slides.length, storyIndex, stories.length,
      leaveSlide, endCurrentStory, openStoryAt, finish,
    ],
  )

  // Стабильная ссылка на advance для авто-перехода: колбэк пересоздаётся при
  // обновлении кеша ленты, но в зависимостях слайд-эффекта его быть не должно.
  const advanceRef = useRef(advance)
  advanceRef.current = advance

  const back = useCallback(() => {
    if (hasAccess && slideIndex > 0) {
      leaveSlide('tap_back')
      setSlideIndex(slideIndex - 1)
    } else if (storyIndex > 0) {
      leaveSlide('tap_back')
      endCurrentStory()
      openStoryAt(storyIndex - 1, 'swipe_prev')
    }
  }, [hasAccess, slideIndex, storyIndex, leaveSlide, endCurrentStory, openStoryAt])

  const close = useCallback(() => {
    leaveSlide('close')
    endCurrentStory()
    finish()
  }, [leaveSlide, endCurrentStory, finish])

  const handleAction = useCallback(
    (s: StorySlide) => {
      // Слайд получает slide_viewed с exit_reason=link, затем link_clicked.
      leaveSlide('link')
      tracker.clickLink({
        slideId: s.slide_id,
        slideIndex: slideIndex + 1,
        targetType: s.action_type as LinkTargetType,
        targetId: s.action_data ?? '',
      })
      endCurrentStory()
      finish()

      const data = s.action_data ?? ''
      switch (s.action_type) {
        case 'url':
          if (data) window.open(data, '_blank', 'noopener')
          break
        case 'course':
          navigate(`/courses/${data}`)
          break
        case 'recipe':
          navigate(`/card/${data}`)
          break
        case 'page':
          navigate(data.startsWith('/') ? data : `/${data}`)
          break
        default:
          break
      }
    },
    [leaveSlide, endCurrentStory, finish, tracker, slideIndex, navigate],
  )

  // Открытие стартовой истории (аналитика) — один раз.
  const openedRef = useRef(false)
  useEffect(() => {
    if (openedRef.current) return
    openedRef.current = true
    tracker.openStory(storyMeta(startIndex), 'tray')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // На каждый слайд: сброс времени показа, отметка просмотра, автопереход картинок.
  // Зависим от slide_id (примитив), а не от объекта slide/колбэка advance — иначе
  // обновление кеша ленты после mark пересоздаёт advance и зациклит POST /view.
  useEffect(() => {
    slideShownAtRef.current = performance.now()
    if (!hasAccess || !slide) return
    scheduleMark(slide.slide_id)
    if (slide.media_type === 'image') {
      const timer = window.setTimeout(() => advanceRef.current('auto'), IMAGE_DURATION_MS)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, slide?.slide_id, storyIndex, slideIndex, scheduleMark])

  // Флаш отметки строго при размонтировании.
  useEffect(
    () => () => {
      const pending = pendingRef.current
      if (pending) {
        clearTimeout(pending.timer)
        markMutateRef.current(pending.slideId)
        pendingRef.current = null
      }
    },
    [],
  )

  // Клавиатура.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowRight') advance('tap_forward')
      else if (e.key === 'ArrowLeft') back()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [close, advance, back])

  if (!story) return null

  return (
    <div className={styles.viewer} role="dialog" aria-modal="true">
      <div className={styles.progress}>
        {slides.map((s, i) => (
          <span
            key={s.slide_id}
            className={[
              styles.seg,
              i < slideIndex && styles.segDone,
              i === slideIndex && styles.segCurrent,
            ]
              .filter(Boolean)
              .join(' ')}
          />
        ))}
      </div>

      <div className={styles.header}>
        <span className={styles.title}>{story.title}</span>
        <button
          type="button"
          className={styles.close}
          onClick={close}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <div className={styles.stage}>
        {!hasAccess || slides.length === 0 ? (
          <div className={styles.locked}>
            {story.access_type === 'subscription'
              ? 'This story is available with a subscription'
              : 'No content'}
          </div>
        ) : slide?.media_type === 'video' ? (
          <video
            key={slide.slide_id}
            className={styles.media}
            src={slide.media_url}
            autoPlay
            playsInline
            onEnded={() => advance('auto')}
          />
        ) : (
          <img
            key={slide?.slide_id}
            className={styles.media}
            src={slide?.media_url}
            alt=""
          />
        )}

        <button
          type="button"
          className={`${styles.tap} ${styles.tapPrev}`}
          onClick={back}
          aria-label="Previous"
        />
        <button
          type="button"
          className={`${styles.tap} ${styles.tapNext}`}
          onClick={() => advance('tap_forward')}
          aria-label="Next"
        />
      </div>

      {slide && slide.action_type && (
        <div className={styles.actionBar}>
          <button
            type="button"
            className={styles.action}
            onClick={() => handleAction(slide)}
          >
            {slide.action_title || 'Open'}
          </button>
        </div>
      )}
    </div>
  )
}

export default StoryViewer
